/**
 * Where a hover card goes so that it covers as few of the other visitors as it can without wandering off (Chris, 2026-10-24: the card for a bubble at the top appeared at the bottom of the demo).
 *
 * The card's usual place (beside the pointer, pushed back inside the window) is kept when it covers no one. Otherwise spots are tried on a grid, but only those whose NEAREST edge is within `maxDistance`
 * of the pointer: a card that crosses the screen to find an empty patch is lost to the eye, so in a thick crowd it covers the fewest visitors it can WITHIN that reach. Among equals the nearest spot wins.
 * A spot that would sit on the pointer itself is never chosen (the card would take the mouse and flicker).
 */
export interface SpotRect { left: number; top: number; right: number; bottom: number }

export interface CardSpotInput {
  box: { left: number; top: number; width: number; height: number };
  pointer: { x: number; y: number };
  others: SpotRect[];
  view: { width: number; height: number; top: number };
  gap?: number;
  maxDistance?: number;
  step?: number;
}

export const chooseCardSpot = ({ box, pointer, others, view, gap = 18, maxDistance = 90, step = 24 }: CardSpotInput): { l: number; t: number } | null => {
  const covered = (l: number, t: number) => {
    const r = l + box.width, b = t + box.height;

    return others.filter((c) => c.right > l && c.left < r && c.bottom > t && c.top < b).length;
  };
  const maxL = view.width - 8 - box.width, maxT = view.height - 8 - box.height;
  // the usual place, as the window will force it: the card is pushed back inside afterwards, so that is where it would really land
  const here = { l: Math.max(8, Math.min(box.left, maxL)), t: Math.max(view.top, Math.min(box.top, maxT)) };
  const hereCount = covered(here.l, here.t);
  if (hereCount === 0) return null;
  const distance = (o: { l: number; t: number }) => Math.hypot(o.l + box.width / 2 - pointer.x, o.t + box.height / 2 - pointer.y);
  let best = here, bestCount = hereCount, bestDist = distance(here);
  for (let l = 8; l <= Math.max(8, maxL); l += step) {
    for (let t = view.top; t <= Math.max(view.top, maxT); t += step) {
      if (pointer.x > l - gap / 2 && pointer.x < l + box.width + gap / 2 && pointer.y > t - gap / 2 && pointer.y < t + box.height + gap / 2) continue;
      const nearX = Math.max(l, Math.min(pointer.x, l + box.width)), nearY = Math.max(t, Math.min(pointer.y, t + box.height));
      if (Math.hypot(nearX - pointer.x, nearY - pointer.y) > maxDistance) continue;
      const n = covered(l, t);
      const d = distance({ l, t });
      if (n < bestCount || (n === bestCount && d < bestDist)) {
        best = { l, t };
        bestCount = n;
        bestDist = d;
      }
    }
  }

  return best === here ? null : best;
};
